import { InputAdornment, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { AccessCard } from '@/components/cards/AccessCard/AccessCard';
import { FormAwareMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareMultiSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Mail01Icon } from '@/components/icons/Mail01Icon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { EmployeeTierSelector } from '@/modules/employees/EmployeeTierSelector';
import { ClientsTypeahead } from '@/modules/household/components/ClientsTypeahead/ClientsTypeahead';
import { TeamsTypeahead } from '@/modules/teams/components/TeamsTypeahead/TeamsTypeahead';
import { EmployeeTier } from '@/types/schema';

import { AssignedClientList } from './AssignedClientList';
import { rolesGrantAllClientAccess } from './EmployeeForm.utils';
import { EmployeeFormShape } from './EmployeeModal';
import { EmployeeForm_RoleFragment } from './graphql/GetEmployee.generated';
import { useFilteredRoleOptions } from './utils';

export interface EmployeeFormProps {
  control: Control<EmployeeFormShape>;
  isCreatingUser: boolean;
  roles: EmployeeForm_RoleFragment[];
  assignedClients: {
    id: string;
    displayName: string;
  }[];
  isSCIMManaged: boolean;
  // Whether to show the IdP ID field (gated by a tenant configuration flag, see EmployeeModal).
  showIdpId: boolean;
}

export function EmployeeForm({
  control,
  assignedClients,
  roles,
  isCreatingUser = false,
  isSCIMManaged = false,
  showIdpId = false,
}: EmployeeFormProps) {
  const roleOptions = useFilteredRoleOptions(roles);
  const canViewHouseholds = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_VIEW_HOUSEHOLDS,
    { detailed: false }
  );

  const { setValue } = useFormContext<EmployeeFormShape>();
  const tier = useWatch({ control, name: 'tier' });
  const selectedRoleIds = useWatch({ control, name: 'roleIds' });
  const lastTier = usePrevious(tier);

  useEffect(() => {
    // We need to clear the roleIds when the tier changes to or from ViewOnly,
    // because the selectable roles are different.
    if (lastTier && tier !== lastTier) {
      const movingToViewOnly = tier === EmployeeTier.ViewOnly;
      const movingFromViewOnly = lastTier === EmployeeTier.ViewOnly;

      if (movingToViewOnly || movingFromViewOnly) {
        setValue('roleIds', []);
      }
    }
  }, [tier, lastTier, setValue]);

  const doesHaveAllClientsAccess = useMemo(() => {
    return rolesGrantAllClientAccess(roles, selectedRoleIds);
  }, [roles, selectedRoleIds]);

  return (
    <Stack component="form" noValidate>
      {isSCIMManaged && (
        <Callout severity="info-high" sx={{ mb: 3 }}>
          This user is managed by SCIM. Deactivate this user through your
          identity provider. Manual assignments to SCIM-managed teams and
          changes to roles or type may be overwritten by your identity provider
          on the next sync. Name changes should be made via SCIM. For email
          changes, please contact Luminary{' '}
          <a href="mailto:support@withluminary.com">support</a>
        </Callout>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <EmployeeTierSelector<EmployeeFormShape>
            control={control}
            fieldName="tier"
            required
            label="Type"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareMultiSelectInput<EmployeeFormShape>
            control={control}
            label="Role(s)"
            fieldName="roleIds"
            options={roleOptions}
            required
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<EmployeeFormShape>
            control={control}
            label="First name"
            required={true}
            fieldName="firstName"
            disabled={isSCIMManaged}
          />
        </FormLayoutItem>
        <FormLayoutItem>
          <FormAwareTextInput<EmployeeFormShape>
            control={control}
            label="Last name"
            required={true}
            fieldName="lastName"
            disabled={isSCIMManaged}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<EmployeeFormShape>
            control={control}
            label="Email"
            type="email"
            fieldName="email"
            required={true}
            disabled={!isCreatingUser || isSCIMManaged}
            helpText={
              isSCIMManaged
                ? 'This user is SCIM-managed. Contact support to change this email address.'
                : isCreatingUser
                  ? ''
                  : 'Contact support to change this email address.'
            }
            startAdornment={
              <InputAdornment position="start">
                <Mail01Icon size={16} />
              </InputAdornment>
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {showIdpId && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTextInput<EmployeeFormShape>
              control={control}
              label="IDP ID"
              fieldName="idpId"
              disabled={isSCIMManaged}
              helpText="Optional: the user's id from their identity provider (e.g., Okta, Entra)"
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {canViewHouseholds ? (
        <FormLayoutRow>
          <FormLayoutItem>
            <AccessCard
              heading="Client access"
              subheading="This user will be given access to any client associated with the selected 'Teams'. Alternatively, you can provide access to individual clients."
            >
              <TeamsTypeahead<EmployeeFormShape>
                control={control}
                fieldName="teamIds"
                label="Teams"
                disableSCIMManagedTeams={false}
              />
              <ClientsTypeahead<EmployeeFormShape>
                control={control}
                disabled={doesHaveAllClientsAccess}
                helpText={
                  doesHaveAllClientsAccess
                    ? "This user's role(s) provide them with access to all clients."
                    : undefined
                }
                fieldName="clientIds"
                label="Individual clients"
              />
            </AccessCard>
          </FormLayoutItem>
        </FormLayoutRow>
      ) : (
        <FormLayoutRow>
          <FormLayoutItem>
            <AssignedClientList clients={assignedClients} />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
    </Stack>
  );
}
